import { type FunctionComponent, useEffect } from "react";
import { isRouteErrorResponse, Scripts, useRouteError } from "react-router";

import { Language, SporProvider } from "@vygruppen/spor-react";

import { datadogRum } from "@datadog/browser-rum";

// 🧱 Root-level ErrorBoundary — catches *everything*
export const RootErrorBoundary: FunctionComponent = () => {
  const error = useRouteError();

  useEffect(() => {
    if (error) {
      datadogRum.addError(error, { source: "RootErrorBoundary" });
    }
  }, [error]);

  // Denne vil kanskje plukke opp 502/504 bad gateway feilene som vi har opplevd
  // TODO: Hvis brukerne faktisk opplever denne ofte, oppdater den slik at den ligner den under
  if (isRouteErrorResponse(error)) {
    return (
      <html lang="nb">
        <head>
          <title>{error.status} — Noe gikk skikkelig galt</title>
        </head>
        <body>
          <div style={{ padding: "2rem", fontFamily: "sans-serif" }}>
            <h1>
              {error.status} {error.statusText}
            </h1>
            <p>{error.data?.message || "En uventet feil oppstod."}</p>
            <a
              href="/"
              style={{
                background: "#00685E",
                textDecoration: "none",
                fontSize: 18,
                height: 54,
                width: 160,
                color: "white",
                borderRadius: 300,
                textAlign: "center",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <span>Last inn på nytt</span>
            </a>
          </div>
          <Scripts />
        </body>
      </html>
    );
  }

  let message = "Unknown error";
  if (error instanceof Error) {
    message = error.message;
  } else if (typeof error === "string") {
    message = error;
  }

  return (
    <html lang="en">
      <head>
        <title>Applikasjonsfeil</title>
      </head>
      <body style={{ padding: 0, margin: 0 }}>
        <SporProvider language={Language.NorwegianBokmal}>
          <div
            style={{
              height: 54,
              background: "#00685E",
            }}
          />
          <div
            style={{
              padding: "1rem",
              fontFamily: `"Vy Sans", sans-serif`,
              display: "flex",
              alignItems: "center",
              flexDirection: "column",
              textAlign: "center",
            }}
          >
            <div
              style={{
                height: 240,
                width: 200,
              }}
            >
              <img src="/illustrations/sorry.svg" alt="" />
            </div>
            <div
              style={{
                maxWidth: 300,
              }}
            >
              <h1
                style={{
                  fontSize: 30,
                  margin: 0,
                  marginTop: 15,
                }}
              >
                Noe gikk skikkelig galt!
              </h1>
              <p
                style={{
                  fontSize: 16,
                  marginBottom: 0,
                  marginTop: 15,
                }}
              >
                Vi klarte ikke å laste innholdet akkurat nå.
              </p>
              <p
                style={{
                  fontSize: 16,
                  margin: 15,
                }}
              >
                Om du vil hjelpe oss å fikse feilen, fortell gjerne hva du
                gjorde rett før den dukket opp.
              </p>
            </div>
            <a
              href="/"
              style={{
                background: "#00685E",
                textDecoration: "none",
                fontSize: 18,
                height: 54,
                width: 160,
                color: "white",
                borderRadius: 300,
                textAlign: "center",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <span>Last inn på nytt</span>
            </a>
            <div
              style={{
                marginTop: 32,
                background: "#f4f4f4",
                border: "1px solid grey",
                borderRadius: 4,
                padding: 12,
                maxWidth: "85vw",
                textAlign: "left",
              }}
            >
              <h2>For utviklerne: </h2>
              <p>{message}</p>
              <pre
                style={{
                  background: "#f4f4f4",
                  border: "1px solid #d0d0d0",
                  padding: "1rem",
                  borderRadius: "8px",
                  whiteSpace: "pre-wrap",
                  maxHeight: 200,
                  overflow: "scroll",
                }}
              >
                {error instanceof Error ? error.stack : null}
              </pre>
            </div>
          </div>
        </SporProvider>
        <Scripts />
      </body>
    </html>
  );
};
